import React, { useCallback, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Drawer, Spin, Tabs, Tooltip } from 'antd';

import axios from 'axios';
import { formatDate } from 'helpers/date';
import { tap } from 'rxjs/operators';
import { URL_ORBITA } from 'settings';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ordersService from 'v2/services/orders';

import { Icon } from '@eduzz/ui-antd-theme';

import Customer from './customer';
import InvoiceItems from './invoiceItems';
import Tracking from './tracking';

enum EInvoiceTabs {
  INVOICE,
  VALUES
}

const Quickview = () => {
  const { message, modal } = App.useApp();
  const [tabInvoice, setTabInvoice] = useState<EInvoiceTabs>(EInvoiceTabs.INVOICE);

  const [isLoading] = useObservable(() => ordersService.isLoadingQuickview(), []);
  const [invoice] = useObservable(() => ordersService.getInvoice(), []);
  const [order] = useObservable(() => ordersService.getOrder(), []);
  const [open] = useObservable(() => ordersService.getQuickview(), []);

  const closeQuickView = useCallback(() => ordersService.closeQuickview(), []);

  const generateLabel = useCallback(async () => {
    const response = await axios.get(`${URL_ORBITA}/orders/tags?invoice_id=${invoice.id}&return_url=1`);
    window.open(response.data.data.download_url, '_blank');
  }, [invoice]);

  const openModalRefund = useCallback(() => ordersService.openModalRefund(), []);

  const [onMarkAsSent] = useCallbackObservable(() => {
    return ordersService.markAsSent().pipe(
      tap(() => {
        try {
          message.success('Entrega marcada como enviada com sucesso!');
        } catch (error) {
          message.error(typeof error === 'string' ? error : 'Não foi possível marcar a entrega como enviada.');
        }
      })
    );
  }, [message]);

  const openModalMarkAsSent = () => {
    modal.confirm({
      content: 'Você tem certeza que deseja marcar essa entrega como enviada?',
      title: 'Atenção',
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      cancelButtonProps: { type: 'text' },
      onOk: onMarkAsSent
    });
  };

  const invoiceTabContent = (
    <div className='rounded-md bg-(--uizz-antd-color-fill-quaternary) p-4 text-sm'>
      <div className='grid grid-cols-1 items-center gap-4 md:grid-cols-3'>
        <div>
          <strong>Código</strong> <span className='block'>{invoice?.id}</span>
        </div>
        <div>
          <strong>Status</strong>
          <span className='block' title={invoice?.payment_status?.name}>
            {invoice?.payment_status?.id !== 6 && invoice?.payment_status?.id !== 15 && invoice?.payment_status?.name}
            {invoice?.payment_status?.id === 6 && 'Ag. Reembolso'}
            {invoice?.payment_status?.id === 15 && 'Ag. Pagamento'}
          </span>
        </div>
        <div>
          {invoice?.created_at && (
            <>
              <strong>Criação</strong>
              <span className='block'>{formatDate(invoice.created_at)}</span>
            </>
          )}
        </div>
        <div>
          {invoice?.received_at && (
            <>
              <strong>Pagamento</strong>
              <span className='block'>{formatDate(invoice.received_at)}</span>
            </>
          )}
        </div>
        <div>
          {invoice?.credited_at && (
            <>
              <strong>Crédito</strong>
              <span className='block'>{formatDate(invoice.credited_at)}</span>
            </>
          )}
        </div>
        <div>
          {invoice?.expired_at && (
            <>
              <strong>Vencimento</strong>
              <span className='block'>{formatDate(invoice.expired_at)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const valuesTabContent = (
    <div className='rounded-md bg-(--uizz-antd-color-fill-quaternary) p-4 text-sm'>
      <div className='grid grid-cols-1 items-center gap-4 md:grid-cols-3'>
        <div>
          <strong className='block'>Venda</strong>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.sale_value} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <strong className='block'>Taxa</strong>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.rate_amount} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <strong className='block'>Comissão</strong>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.commission_amount} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <strong className='block'>Parceiro</strong>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.partner_amount} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <Tooltip
            placement='top'
            title='A antecipação é descontada diretamente no extrato financeiro (valor apenas para conhecimento)'
          >
            <strong className='flex items-center gap-1'>
              Antecipação <Icon.Info size={14} />
            </strong>
          </Tooltip>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.anticipation_amount} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <strong className='block'>Outros</strong>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.others_values} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
      </div>
    </div>
  );

  return (
    <Drawer
      onClose={closeQuickView}
      placement='right'
      open={open}
      closable={false}
      width='min(450px, 100vw)'
      title={`Fatura: ${invoice?.id}`}
      extra={
        <Button type='text' shape='circle' aria-label='Fechar' icon={<Icon.X size={16} />} onClick={closeQuickView} />
      }
    >
      {isLoading && (
        <div className='flex items-center justify-center py-8'>
          <Spin />
        </div>
      )}
      {!isLoading && invoice && (
        <div className='flex flex-col gap-4'>
          <Tabs
            size='small'
            activeKey={String(tabInvoice)}
            onChange={key => setTabInvoice(Number(key) as EInvoiceTabs)}
            items={[
              { key: String(EInvoiceTabs.INVOICE), label: 'Fatura', children: invoiceTabContent },
              { key: String(EInvoiceTabs.VALUES), label: 'Valores', children: valuesTabContent }
            ]}
          />
          <Customer />
          <InvoiceItems />
          <Tracking />
          <div className='flex flex-col gap-2'>
            <PermissionHide action='myeduzz:minhas-entregas:editar'>
              <Button color='default' variant='filled' block onClick={generateLabel}>
                Gerar Etiqueta
              </Button>
            </PermissionHide>
            <PermissionHide action='myeduzz:minhas-entregas:reembolsar'>
              {invoice.payment_status?.id === 3 && (
                <Button color='default' variant='filled' block onClick={openModalRefund}>
                  Reembolsar fatura
                </Button>
              )}
            </PermissionHide>
            <PermissionHide action='myeduzz:minhas-entregas:editar'>
              {order?.status === 'Aguardando Envio' && (
                <Button type='primary' block onClick={openModalMarkAsSent}>
                  Marcar como enviado
                </Button>
              )}
            </PermissionHide>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default Quickview;
